import type { SeatFinderMap, SeatFinderTable } from '@peles/shared';

const ROUND_SHAPES = new Set(['CIRCLE', 'OVAL']);

function TableMark({ table }: { table: SeatFinderTable }) {
  const cx = table.x + table.widthFt / 2;
  const cy = table.y + table.heightFt / 2;
  const fontSize = Math.max(Math.min(table.widthFt, table.heightFt) * 0.45, 1.2);
  const shapeClass = table.mine
    ? 'fill-primary-600 stroke-primary-800'
    : 'fill-gray-200 stroke-gray-400';
  return (
    <g
      transform={`rotate(${table.rotation}, ${cx}, ${cy})`}
      data-testid={table.mine ? 'seat-map-mine' : 'seat-map-table'}
    >
      {ROUND_SHAPES.has(table.shape ?? '') ? (
        <ellipse
          cx={cx}
          cy={cy}
          rx={table.widthFt / 2}
          ry={table.heightFt / 2}
          className={shapeClass}
          strokeWidth={0.2}
        />
      ) : (
        <rect
          x={table.x}
          y={table.y}
          width={table.widthFt}
          height={table.heightFt}
          rx={0.3}
          className={shapeClass}
          strokeWidth={0.2}
        />
      )}
      {table.tableNumber != null && (
        <text
          x={cx}
          y={cy}
          textAnchor="middle"
          dominantBaseline="central"
          fontSize={fontSize}
          transform={`rotate(${-table.rotation}, ${cx}, ${cy})`}
          className={table.mine ? 'fill-white font-semibold' : 'fill-gray-600'}
        >
          {table.tableNumber}
        </text>
      )}
    </g>
  );
}

/**
 * The room drawn small with the guest's table filled in — enough to walk
 * towards, never who sits anywhere else. Drawn from the seating plan's own
 * positions in feet, so it matches the room the office laid out.
 */
export function SeatFinderMiniMap({ map }: { map: SeatFinderMap }) {
  const mine = map.tables.find((table) => table.mine);
  const pad = 1;
  return (
    <figure className="mt-6">
      <svg
        viewBox={`${-pad} ${-pad} ${map.widthFt + pad * 2} ${map.heightFt + pad * 2}`}
        className="w-full max-h-80 rounded-xl bg-gray-50"
        role="img"
        aria-label={
          mine?.tableNumber != null
            ? `Room map with table ${mine.tableNumber} highlighted`
            : 'Room map with your table highlighted'
        }
      >
        {map.outline ? (
          <polygon
            points={map.outline.map((p) => `${p.x},${p.y}`).join(' ')}
            className="fill-white stroke-gray-500"
            strokeWidth={0.3}
            data-testid="seat-map-room"
          />
        ) : (
          <rect
            x={0}
            y={0}
            width={map.widthFt}
            height={map.heightFt}
            className="fill-white stroke-gray-500"
            strokeWidth={0.3}
            data-testid="seat-map-room"
          />
        )}
        {map.tables
          .filter((table) => !table.mine)
          .map((table, index) => (
            <TableMark key={index} table={table} />
          ))}
        {mine && <TableMark table={mine} />}
      </svg>
      <figcaption className="mt-2 text-xs text-gray-500">
        Your table is filled in. The room is drawn from above.
      </figcaption>
    </figure>
  );
}
